import React, { useEffect, useMemo, useRef, useState } from "react";
import { Easing, Platform, Pressable, StyleSheet, View, useWindowDimensions, type ViewStyle } from "react-native";
import { AnimatePresence, MotiView } from "moti";
import { createPortal } from "react-dom";
import { ArrowRightIcon, DownloadSimpleIcon, SparkleIcon, XIcon } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import {
  ModernizedEvaluatorIssueStatus,
  useGetModernizedCourseQuery,
  useUpdateModernizedEvaluatorIssueStatusMutation,
  type GetModernizedCourseQuery,
} from "@axio/graphql/generated/graphql";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import GapAnalysisImportIssuesPage, {
  type GapAnalysisImportIssue,
} from "../../../../../components/adminDashboard/CourseModernizer/GapAnalysisImportIssuesPage";
import GapAnalysisResyncModal from "../../../../../components/adminDashboard/CourseModernizer/GapAnalysisResyncModal";
import GapAnalysisVerticalLayout from "../../../../../components/adminDashboard/CourseModernizer/GapAnalysisVerticalLayout";
import { colors } from "../../../../../styles/design-system";
import type { CourseEvaluatorReviewData, CourseEvaluatorReviewSidebarItem } from "../types/reviewTypes";
import { DimensionCell } from "./DimensionCell";
import { DimensionIssuePanel } from "./DimensionIssuePanel";
import GapActionDrawerSlideout, { type GapActionDrawerVariant } from "./GapActionDrawerSlideout";
import GapAnalysisDimensionRail from "./GapAnalysisDimensionRail";
import GapAnalysisIssueEditor from "./GapAnalysisIssueEditor";
import { DIMENSION_ID_ALIASES, GAP_DIMENSIONS, type GapAnalysisDimension } from "./gapAnalysisConstants";
import { useDownloadGapAnalysisReport } from "./GapAnalysisReport";
import { getOverriddenDimensionLabel } from "./gapAnalysisUtils";

interface GapAnalysisProps {
  review: CourseEvaluatorReviewData;
  onStartReviewing?: () => void;
  onRerun?: () => void | Promise<void>;
  onSelectDimension?: (dimensionId: string) => void;
  onOverlayVisibilityChange?: (isVisible: boolean) => void;
  onAccept?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onReject?: (item: CourseEvaluatorReviewSidebarItem) => void;
  // Accept/reject without navigation (for Gap Analysis drawer)
  onAcceptWithoutNav?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onRejectWithoutNav?: (item: CourseEvaluatorReviewSidebarItem) => void;
  onJumpToEntity?: (entityId: string, dimension: string) => void;
}

type ModernizedEvaluatorIssue = NonNullable<
  NonNullable<GetModernizedCourseQuery["getModernizedCourse"]>["evaluatorIssues"]
>[number];

function normalizeDimensionId(dimensionId: string): string {
  const normalized = dimensionId
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-");
  return DIMENSION_ID_ALIASES[normalized] ?? normalized;
}

function getDimensionStatusFromScore(score: number): GapAnalysisDimension["status"] {
  if (score >= 90) {
    return "Strong";
  }

  if (score >= 75) {
    return "Good";
  }

  if (score >= 60) {
    return "Needs work";
  }

  return "Critical";
}

function getDimensionToneFromStatus(status: GapAnalysisDimension["status"]): GapAnalysisDimension["tone"] {
  if (status === "Strong") {
    return "success";
  }

  if (status === "Good") {
    return "brand";
  }

  if (status === "Needs work") {
    return "warning";
  }

  return "error";
}

/**
 * Helper function to add entity data to dimension issues from evaluator issues
 */
function addEntityDataToIssues(
  dimension: GapAnalysisDimension,
  evaluatorIssues: ModernizedEvaluatorIssue[]
): GapAnalysisDimension {
  const allDimensionIssues = evaluatorIssues.filter((issue) => normalizeDimensionId(issue.dimension) === dimension.id);
  const filteredIssues = allDimensionIssues.slice(0, 4);

  const issuesWithEntity = filteredIssues
    .filter((issue) => issue.description && issue.description.trim().length > 0)
    .map((issue) => ({
      id: issue.id,
      description: issue.description,
      entityId: issue.entityId,
      entityType: issue.entityType,
      title: issue.title,
    }));

  return {
    ...dimension,
    issues: filteredIssues.length,
    issuesList: issuesWithEntity.map((issue) => issue.description),
    issuesWithEntity,
  };
}

function mapDimensionsFromEvaluatorScores(review: CourseEvaluatorReviewData): GapAnalysisDimension[] {
  const evaluatorDimensionScores = review.evaluatorDimensionScores ?? [];
  const scoresByDimensionId = new Map(
    evaluatorDimensionScores.map((dimensionScore) => [normalizeDimensionId(dimensionScore.dimension), dimensionScore])
  );

  return GAP_DIMENSIONS.map((dimension) => {
    const scorecardDimension = scoresByDimensionId.get(dimension.id);
    if (!scorecardDimension) {
      // Keep static dimension state when no evaluator data exists
      // Apply dynamic label override for rubric-quality dimension
      return getOverriddenDimensionLabel(dimension, review.frameworkName);
    }

    const normalizedScore =
      scorecardDimension.maxScore > 0 ? Math.round((scorecardDimension.score / scorecardDimension.maxScore) * 100) : 0;
    const status = getDimensionStatusFromScore(normalizedScore);
    const mappedDimension = {
      ...dimension,
      calculatedScore: normalizedScore,
      score: scorecardDimension.score,
      maxScore: scorecardDimension.maxScore,
      status,
      tone: getDimensionToneFromStatus(status),
      issues: scorecardDimension.issueCount,
      summary: scorecardDimension.summary,
      detailedFindings: scorecardDimension.detailedFindings,
      evaluatedEntities: scorecardDimension.evaluatedEntities,
      failedEntities: scorecardDimension.failedEntities,
      goodEntities: scorecardDimension.goodEntities,
    };

    // Apply dynamic label override for rubric-quality dimension
    return getOverriddenDimensionLabel(mappedDimension, review.frameworkName);
  });
}

function buildHighLevelSummarySentence(moduleCount: number, totalCount: number, hasFlaggedIssues: boolean): string {
  if (!hasFlaggedIssues) {
    return `AI reviewed ${totalCount} activities across ${moduleCount} modules. No flagged issues were found in this run.`;
  }

  return `AI reviewed ${totalCount} activities across ${moduleCount} modules. Start reviewing to address the flagged issues.`;
}

function GapAnalysis({
  review,
  onStartReviewing,
  onRerun,
  onSelectDimension,
  onOverlayVisibilityChange,
  onAccept,
  onReject,
  onAcceptWithoutNav,
  onRejectWithoutNav,
  onJumpToEntity,
}: GapAnalysisProps) {
  const DRAWER_EXIT_ANIMATION_MS = 300;
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isCompact = width < 760;
  const importIssuesOpenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch evaluator issues and dimension scores from GraphQL
  // Skip query if courseId is invalid or in test environment
  const shouldSkipQuery = !review.courseId || review.courseId.startsWith("course-");
  const { data: modernizedCourseData, refetch: refetchModernizedCourse } = useGetModernizedCourseQuery({
    variables: { courseId: review.courseId },
    fetchPolicy: "cache-and-network",
    skip: shouldSkipQuery,
  });
  const queriedModernizedCourse = modernizedCourseData?.getModernizedCourse;
  const evaluatorIssues: ModernizedEvaluatorIssue[] = queriedModernizedCourse?.evaluatorIssues ?? [];
  const evaluatorDimensionScores = queriedModernizedCourse?.evaluatorDimensionScores ?? [];

  // Mutation for updating evaluator issue status
  const [updateEvaluatorIssueStatus] = useUpdateModernizedEvaluatorIssueStatusMutation();

  // Combine dimensions from scores with entity data from issues
  const dimensions = useMemo(() => {
    const reviewWithScores = { ...review, evaluatorDimensionScores };
    const dimensionsWithScores = mapDimensionsFromEvaluatorScores(reviewWithScores);
    return dimensionsWithScores.map((dimension) => addEntityDataToIssues(dimension, evaluatorIssues));
  }, [review, evaluatorIssues, evaluatorDimensionScores]);

  const [selectedDimensionId, setSelectedDimensionId] = useState<string | null>(null);
  const [activeDrawerVariant, setActiveDrawerVariant] = useState<GapActionDrawerVariant | null>(null);
  const [isImportIssuesVisible, setIsImportIssuesVisible] = useState(false);
  const [isResyncModalVisible, setIsResyncModalVisible] = useState(false);
  const [isImportIssuesResynced, setIsImportIssuesResynced] = useState(false);
  const [editingEntity, setEditingEntity] = useState<{ entityId: string; entityType: string } | null>(null);
  const moduleCount = review.sidebarGroups.length;
  const hasFlaggedIssues = dimensions.some((dimension) => dimension.issues > 0);
  const highLevelSummarySentence = buildHighLevelSummarySentence(moduleCount, review.totalCount, hasFlaggedIssues);
  const handleDownloadReport = useDownloadGapAnalysisReport({
    courseName: review.courseName,
    courseCode: review.courseCode,
    termLabel: review.termLabel,
    frameworkName: review.frameworkName,
    reviewedDateLabel: review.reviewedDateLabel,
    dimensions,
  });
  const selectedDimension = dimensions.find((dimension) => dimension.id === selectedDimensionId) ?? null;
  const dimensionRows = getDimensionRows(dimensions, isCompact);
  const isScorecardLayoutVisible = activeDrawerVariant === "gap-analysis-scorecard";
  const hasBlockingOverlay = isImportIssuesVisible || activeDrawerVariant !== null;

  useEffect(() => {
    onOverlayVisibilityChange?.(hasBlockingOverlay);

    return () => {
      onOverlayVisibilityChange?.(false);
    };
  }, [hasBlockingOverlay, onOverlayVisibilityChange]);

  useEffect(() => {
    return () => {
      if (importIssuesOpenTimeoutRef.current) {
        clearTimeout(importIssuesOpenTimeoutRef.current);
      }
    };
  }, []);

  const handleSelectDimension = (dimensionId: string) => {
    setSelectedDimensionId((currentDimensionId) => (currentDimensionId === dimensionId ? null : dimensionId));
    onSelectDimension?.(dimensionId);
  };

  const handleShowGapAnalysisScorecard = (dimensionId: string) => {
    setSelectedDimensionId(dimensionId);
    setActiveDrawerVariant("gap-analysis-scorecard");
    onSelectDimension?.(dimensionId);
  };

  const handleCloseImportIssues = () => {
    setIsImportIssuesVisible(false);
  };

  const handleOpenImportIssues = () => {
    if (importIssuesOpenTimeoutRef.current) {
      clearTimeout(importIssuesOpenTimeoutRef.current);
      importIssuesOpenTimeoutRef.current = null;
    }

    if (isImportIssuesVisible) {
      setIsImportIssuesVisible(false);
      return;
    }

    if (activeDrawerVariant !== null) {
      setActiveDrawerVariant(null);
      importIssuesOpenTimeoutRef.current = setTimeout(() => {
        setIsImportIssuesVisible(true);
        importIssuesOpenTimeoutRef.current = null;
      }, DRAWER_EXIT_ANIMATION_MS);
      return;
    }

    setIsImportIssuesVisible(true);
  };

  const handleOpenResyncModal = () => {
    setIsResyncModalVisible(true);
  };

  const handleCloseResyncModal = () => {
    setIsResyncModalVisible(false);
  };

  const handleShowDismissedImportIssues = () => {
    setIsResyncModalVisible(false);
    setIsImportIssuesResynced(false);
    setIsImportIssuesVisible(true);
  };

  const handleConfirmResync = async () => {
    setIsResyncModalVisible(false);
    setIsImportIssuesVisible(true);
    setIsImportIssuesResynced(true);
  };

  const handleAddToQueue = async () => {
    // If no dimension is selected or no issues, do nothing
    if (!selectedDimensionId) {
      return;
    }

    // Find all issues for the selected dimension
    const dimensionIssues = evaluatorIssues.filter(
      (issue) => normalizeDimensionId(issue.dimension) === selectedDimensionId
    );

    if (dimensionIssues.length === 0) {
      return;
    }

    try {
      // Update all issues for this dimension to "FIXED" status
      await Promise.all(
        dimensionIssues.map((issue) =>
          updateEvaluatorIssueStatus({
            variables: {
              issueId: issue.id,
              status: ModernizedEvaluatorIssueStatus.Fixed,
            },
          })
        )
      );

      // Refetch to get updated dimension scores
      await refetchModernizedCourse();
    } catch (error) {
      console.error("Failed to update issue statuses:", error);
    }
  };

  const handleSkip = async () => {
    // If no dimension is selected or no issues, do nothing
    if (!selectedDimensionId) {
      return;
    }

    // Find all issues for the selected dimension
    const dimensionIssues = evaluatorIssues.filter(
      (issue) => normalizeDimensionId(issue.dimension) === selectedDimensionId
    );

    if (dimensionIssues.length === 0) {
      return;
    }

    try {
      // Update all issues for this dimension to "WAIVED" status
      await Promise.all(
        dimensionIssues.map((issue) =>
          updateEvaluatorIssueStatus({
            variables: {
              issueId: issue.id,
              status: ModernizedEvaluatorIssueStatus.Waived,
            },
          })
        )
      );

      // Refetch to get updated dimension scores
      await refetchModernizedCourse();
    } catch (error) {
      console.error("Failed to update issue statuses:", error);
    }
  };

  const handleEdit = (entityId: string, entityType: string) => {
    setEditingEntity({ entityId, entityType });
  };

  const handleCloseEditor = () => {
    setEditingEntity(null);
    // Refetch to show updated data after edit
    refetchModernizedCourse();
  };

  const importIssues: GapAnalysisImportIssue[] =
    evaluatorIssues.length > 0
      ? evaluatorIssues.map((issue) => ({
          id: issue.id,
          source: issue.dimensionLabel || "",
          reason: issue.title || "",
          description: issue.description || "",
        }))
      : review.documentIssues.map((issue) => ({
          id: issue.id,
          source: issue.title,
          reason: getImportIssueReason(issue.documentIssueKind),
          description: issue.message,
        }));

  return (
    <>
      <View style={[styles.reviewCallout, isCompact && styles.reviewCalloutCompact]}>
        <View style={styles.reviewCalloutContent}>
          <View style={styles.reviewCalloutIcon}>
            <SparkleIcon size={16} color={colors.brand[600]} weight="bold" />
          </View>
          <View style={styles.reviewCalloutTextGroup}>
            <CustomText
              text="Gap analysis overview"
              useTranslationText={false}
              size="s"
              weight="medium"
              style={styles.reviewCalloutTitle}
            />
            <CustomText
              text={highLevelSummarySentence}
              useTranslationText={false}
              size="s"
              weight="normal"
              style={styles.reviewCalloutDescription}
            />
          </View>
        </View>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Start reviewing"
          onPress={onStartReviewing}
          style={(state) => [
            styles.reviewCalloutButton,
            isCompact && styles.reviewCalloutButtonCompact,
            getBrandButtonInteractionStyle(state),
          ]}>
          <CustomText
            text="Start reviewing"
            useTranslationText={true}
            size="s"
            weight="medium"
            style={styles.reviewCalloutButtonText}
          />
          <ArrowRightIcon size={16} color={colors.white} />
        </Pressable>
      </View>

      <View style={styles.card}>
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <CustomText
              text="Quality dimensions"
              useTranslationText={false}
              size="s"
              weight="medium"
              style={styles.headerTitle}
            />
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("text.Download gap analysis report as PDF")}
            onPress={handleDownloadReport}
            style={(state) => [styles.downloadReportButton, getSurfaceButtonInteractionStyle(state)]}>
            <DownloadSimpleIcon size={14} color={colors.grey[700]} />
            <CustomText
              text="Download report"
              useTranslationText={true}
              size="xs"
              weight="medium"
              style={styles.downloadReportText}
            />
          </Pressable>
        </View>

        <View style={styles.divider} />

        <View>
          {dimensionRows.map((row, rowIndex) => {
            const selectedRowDimension = row.find((dimension) => dimension.id === selectedDimensionId) ?? null;

            return (
              <View key={row.map((dimension) => dimension.id).join("-")}>
                <View style={styles.dimensionRow}>
                  {row.map((dimension, index) => (
                    <DimensionCell
                      key={dimension.id}
                      dimension={dimension}
                      isFirstColumn={index === 0}
                      isCompact={isCompact}
                      isSelected={dimension.id === selectedDimensionId}
                      onSelectDimension={handleSelectDimension}
                    />
                  ))}
                </View>
                {selectedRowDimension && (
                  <DimensionIssuePanel dimension={selectedRowDimension} evaluatorIssues={evaluatorIssues} />
                )}
                {rowIndex < dimensionRows.length - 1 && <View style={styles.divider} />}
              </View>
            );
          })}
        </View>

        <View style={styles.divider} />
        {!selectedDimension && (
          <View style={styles.footer}>
            <CustomText
              text="Click a dimension to view priority items and recommended edits."
              useTranslationText={false}
              size="xs"
              weight="normal"
              style={styles.footerHint}
            />
          </View>
        )}
      </View>

      <AnimatePresence>
        {isScorecardLayoutVisible && (
          <ScorecardLayoutSlideout onClose={() => setActiveDrawerVariant(null)}>
            <GapAnalysisVerticalLayout>
              <GapAnalysisDimensionRail
                review={review}
                dimensions={dimensions}
                selectedDimensionId={selectedDimensionId}
                onSelectDimension={handleSelectDimension}
                onJumpToEntity={onJumpToEntity}
                headerLabel="Gap analysis"
              />
            </GapAnalysisVerticalLayout>
          </ScorecardLayoutSlideout>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeDrawerVariant && activeDrawerVariant !== "gap-analysis-scorecard" && (
          <GapActionDrawerSlideout
            review={review}
            dimensions={dimensions}
            variant={activeDrawerVariant}
            selectedDimensionId={selectedDimensionId}
            onSelectDimension={handleSelectDimension}
            onShowGapAnalysisScorecard={handleShowGapAnalysisScorecard}
            onBack={() => setActiveDrawerVariant(null)}
            onClose={() => setActiveDrawerVariant(null)}
            onAddToQueue={handleAddToQueue}
            onSkip={handleSkip}
            onJumpToEntity={onJumpToEntity}
            onEdit={handleEdit}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isImportIssuesVisible && (
          <ImportIssuesSlideout onClose={handleCloseImportIssues}>
            <GapAnalysisImportIssuesPage
              issues={importIssues}
              onDismissIssue={() => undefined}
              onClose={handleCloseImportIssues}
              onResync={handleOpenResyncModal}
              isResyncComplete={isImportIssuesResynced}
              width={460}
            />
          </ImportIssuesSlideout>
        )}
      </AnimatePresence>

      <GapAnalysisResyncModal
        visible={isResyncModalVisible}
        dismissedCount={0}
        onShowDismissed={handleShowDismissedImportIssues}
        onCancel={handleCloseResyncModal}
        onResync={handleConfirmResync}
      />

      <AnimatePresence>
        {editingEntity && (
          <EditorSlideout onClose={handleCloseEditor}>
            <GapAnalysisIssueEditor
              entityId={editingEntity.entityId}
              entityType={editingEntity.entityType}
              entityTitle="Entity"
              onClose={handleCloseEditor}
            />
          </EditorSlideout>
        )}
      </AnimatePresence>
    </>
  );
}

function getImportIssueReason(documentIssueKind: string) {
  if (documentIssueKind === "permissionDenied") {
    return "Permission denied";
  }

  if (documentIssueKind === "brokenLink") {
    return "Broken link";
  }

  return "Import issue";
}

function ScorecardLayoutSlideout({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  const slideout = (
    <MotiView
      from={{ translateX: 720 }}
      animate={{ translateX: 0 }}
      exit={{ translateX: 720 }}
      transition={{ type: "timing", duration: 500, easing: Easing.out(Easing.ease) }}
      exitTransition={{ type: "timing", duration: 300, easing: Easing.in(Easing.ease) }}
      style={styles.scorecardSlideout}
      role="dialog"
      accessibilityLabel="Gap analysis dimensions">
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Close gap analysis dimensions"
        onPress={onClose}
        style={styles.scorecardCloseButton}>
        <XIcon size={16} color={colors.grey[700]} />
      </Pressable>
      {children}
    </MotiView>
  );

  if (Platform.OS === "web" && typeof document !== "undefined") {
    return createPortal(slideout, document.body);
  }

  return slideout;
}

function ImportIssuesSlideout({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  const slideout = (
    <MotiView
      from={{ translateX: 460 }}
      animate={{ translateX: 0 }}
      exit={{ translateX: 460 }}
      transition={{ type: "timing", duration: 500, easing: Easing.out(Easing.ease) }}
      exitTransition={{ type: "timing", duration: 300, easing: Easing.in(Easing.ease) }}
      style={styles.importIssuesSlideout}
      role="dialog"
      accessibilityLabel="Import issues">
      {children}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Close import issues"
        onPress={onClose}
        style={styles.importIssuesCloseButton}>
        <XIcon size={16} color={colors.grey[700]} />
      </Pressable>
    </MotiView>
  );

  if (Platform.OS === "web" && typeof document !== "undefined") {
    return createPortal(slideout, document.body);
  }

  return slideout;
}

function EditorSlideout({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  const slideout = (
    <MotiView
      from={{ translateX: 600 }}
      animate={{ translateX: 0 }}
      exit={{ translateX: 600 }}
      transition={{ type: "timing", duration: 500, easing: Easing.out(Easing.ease) }}
      exitTransition={{ type: "timing", duration: 300, easing: Easing.in(Easing.ease) }}
      style={styles.editorSlideout}
      role="dialog"
      accessibilityLabel="Edit entity">
      {children}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Close editor"
        onPress={onClose}
        style={styles.editorCloseButton}>
        <XIcon size={16} color={colors.grey[700]} />
      </Pressable>
    </MotiView>
  );

  if (Platform.OS === "web" && typeof document !== "undefined") {
    return createPortal(slideout, document.body);
  }

  return slideout;
}

export function getDimensionRows(dimensions: GapAnalysisDimension[], isCompact: boolean) {
  if (isCompact) {
    return dimensions.map((dimension) => [dimension]);
  }

  const [firstDimension, ...remainingDimensions] = dimensions;

  if (!firstDimension) {
    return [];
  }

  const rows: GapAnalysisDimension[][] = [[firstDimension]];

  for (let index = 0; index < remainingDimensions.length; index += 2) {
    rows.push(remainingDimensions.slice(index, index + 2));
  }

  return rows;
}

function getSurfaceButtonInteractionStyle({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) {
  return {
    backgroundColor: hovered ? colors.grey[25] : colors.white,
    shadowOpacity: hovered ? 0.1 : 0.05,
    shadowRadius: hovered ? 8 : 4,
    transform: [{ scale: pressed ? 0.985 : hovered ? 1.015 : 1 }],
  };
}

function getTransparentButtonInteractionStyle({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) {
  return {
    backgroundColor: hovered || pressed ? colors.grey[100] : "transparent",
    transform: [{ scale: pressed ? 0.985 : hovered ? 1.015 : 1 }],
  };
}

function getBrandButtonInteractionStyle({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) {
  return {
    backgroundColor: pressed ? colors.brand[700] : hovered ? colors.brand[600] : colors.brand[500],
    transform: [{ scale: pressed ? 0.985 : hovered ? 1.015 : 1 }],
  };
}

const styles = StyleSheet.create({
  reviewCallout: {
    backgroundColor: colors.grey[25],
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 18,
    paddingHorizontal: 20,
    paddingVertical: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  reviewCalloutCompact: {
    alignItems: "stretch",
  },
  reviewCalloutContent: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  reviewCalloutIcon: {
    width: 32,
    height: 32,
    borderRadius: 999,
    backgroundColor: colors.brand[50],
    borderWidth: 1,
    borderColor: colors.brand[100],
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  reviewCalloutTextGroup: {
    minWidth: 0,
    flex: 1,
  },
  reviewCalloutTitle: {
    color: colors.grey[900],
    lineHeight: 20,
  },
  reviewCalloutDescription: {
    color: colors.grey[800],
    lineHeight: 20,
    marginTop: 2,
  },
  reviewCalloutButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    backgroundColor: colors.brand[500],
    borderRadius: 6,
    paddingHorizontal: 14,
    paddingVertical: 8,
    flexShrink: 0,
  },
  reviewCalloutButtonCompact: {
    width: "100%",
  },
  reviewCalloutButtonText: {
    color: colors.white,
  },
  card: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 18,
    overflow: "hidden",
    shadowColor: colors.grey[900],
    shadowOpacity: 0.06,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
  },
  scorecardSlideout: {
    width: "100%",
    maxWidth: 540,
    height: "100%",
    backgroundColor: colors.white,
    borderLeftWidth: 1,
    borderLeftColor: colors.grey[200],
    ...(Platform.OS === "web"
      ? ({
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          height: "100vh",
          zIndex: 1000,
        } as unknown as ViewStyle)
      : {
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
        }),
  },
  importIssuesSlideout: {
    width: "100%",
    maxWidth: 460,
    height: "100%",
    backgroundColor: colors.white,
    borderLeftWidth: 1,
    borderLeftColor: colors.grey[200],
    ...(Platform.OS === "web"
      ? ({
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          height: "100vh",
          zIndex: 1001,
        } as unknown as ViewStyle)
      : {
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 1001,
        }),
  },
  scorecardCloseButton: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    opacity: 0.7,
    zIndex: 41,
  },
  importIssuesCloseButton: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    opacity: 0.7,
    zIndex: 43,
  },
  editorSlideout: {
    width: "100%",
    maxWidth: 600,
    height: "100%",
    backgroundColor: colors.white,
    borderLeftWidth: 1,
    borderLeftColor: colors.grey[200],
    padding: 24,
    ...(Platform.OS === "web"
      ? ({
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          height: "100vh",
          zIndex: 1002,
          overflowY: "auto",
        } as unknown as ViewStyle)
      : {
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 1002,
        }),
  },
  editorCloseButton: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 24,
    height: 24,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    opacity: 0.7,
    zIndex: 44,
  },
  header: {
    minHeight: 76,
    paddingHorizontal: 24,
    paddingVertical: 18,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    backgroundColor: colors.grey[25],
  },
  headerLeft: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  headerTitle: {
    color: colors.grey[900],
    fontSize: 14,
    lineHeight: 18,
  },
  downloadReportButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.1)",
    borderRadius: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    shadowColor: colors.grey[900],
    shadowOpacity: 0.05,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
  },
  downloadReportText: {
    color: colors.grey[700],
  },
  divider: {
    height: 1,
    backgroundColor: colors.grey[200],
  },
  dimensionRow: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  footer: {
    paddingHorizontal: 24,
    paddingVertical: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
    flexWrap: "wrap",
  },
  footerHint: {
    color: colors.grey[500],
    textAlign: "right",
  },
});

export default GapAnalysis;
